/**
 * Pure helpers for the Azure AI Speech real-time connection.
 *
 * Kept separate from index.tsx so the reconnect/liveness math is testable
 * without standing up a full ConversationTranscriber / AudioContext mock.
 * The component remains the orchestrator; this file is the rule book.
 *
 * Replaces the AssemblyAI-specific WebSocket URL builder that lived here
 * previously — Azure's browser SDK builds its own connection internally
 * from a SpeechConfig + AudioConfig, so there's no URL to construct. The
 * reconnect/liveness primitives below are provider-agnostic and carry over
 * unchanged.
 */

export const SAMPLE_RATE = 16000;

// Real-time speaker diarization via Azure's ConversationTranscriber. Enabled
// per-session by setting `maxSpeakerCount` on the SpeechConfig (via
// setProperty, NOT a constructor/URL param) when a caller requests it.
// Azure's diarization tags each final result with a `speakerId` (format
// TBD — verify against a live session; e.g. "Guest-1"). Accuracy beyond 2
// speakers is not guaranteed per Azure's own docs — acceptable for our
// primary 2-speaker (examiner + examinee) case.
export const MAX_SPEAKERS_LIMIT = 10;

export type StreamingOptions = {
  /**
   * When set (>= 1), turns on speaker diarization and hints the expected
   * speaker count. Omit / undefined → diarization off (current behaviour).
   */
  maxSpeakers?: number;
  /**
   * Case this recording session is for (SCRIBE-02). When set, the backend
   * requires case ownership + a confirmed, active recording consent before
   * issuing a streaming token — see `fetchAzureSpeechToken` in index.tsx.
   * Omit for sessions that don't record an examinee (e.g. solo report
   * dictation), which need no consent check.
   */
  caseNumber?: string;
};

/**
 * Clamp a requested speaker count into Azure's supported range. Returns
 * undefined when diarization shouldn't be enabled at all (no hint given).
 */
export function clampMaxSpeakers(
  maxSpeakers: number | undefined
): number | undefined {
  if (maxSpeakers == null || maxSpeakers < 1) return undefined;
  return Math.min(Math.max(1, Math.round(maxSpeakers)), MAX_SPEAKERS_LIMIT);
}

// Azure Speech authorization tokens are valid for ~10 minutes. Refresh with
// margin so a long assessment (up to hours) never hits an expired token
// mid-session. Must be set on BOTH the SpeechConfig and the live
// ConversationTranscriber instance — see index.tsx's token-refresh timer.
export const AZURE_TOKEN_REFRESH_MS = 9 * 60 * 1000;

// Bumped from 3 → 10 (kept from the AssemblyAI implementation). Three
// attempts at 1/2/3s leaves the session dead after ~6 seconds of network
// trouble, which is unforgiving for a dictation that may already be 45
// minutes deep.
export const MAX_RECONNECT_ATTEMPTS = 10;
export const RECONNECT_BACKOFF_BASE_MS = 1000;
export const RECONNECT_BACKOFF_MAX_MS = 30000;

// How long a live session tolerates total silence (nobody speaking, but the
// mic/connection are otherwise fine) before treating it as an actual
// problem rather than a normal pause in an assessment. Drives the "still
// there?" modal (index.tsx) so a real hour-long silence never nags the
// doctor. Deliberately NOT used by the liveness watchdog below — see
// isVoiceLevel for why those are different failure modes.
export const SILENCE_TOLERANCE_MS = 60 * 60 * 1000; // 1 hour

// Coarse energy gate distinguishing "someone is actually talking" from
// ambient room noise/silence, using the same peak-amplitude value already
// computed for the mic-level UI ring (see index.tsx's worklet handler) —
// no extra audio tap needed. Not a real VAD; just enough to keep the
// liveness watchdog below from treating a quiet room as "frames flowing".
// Peak is normalised 0..1 (Int16 sample / 32768). May need tuning against
// real hardware/room noise floors.
export const LIVENESS_VOICE_PEAK_THRESHOLD = 0.02;

export function isVoiceLevel(peak: number): boolean {
  return peak >= LIVENESS_VOICE_PEAK_THRESHOLD;
}

// Liveness watchdog: if we have seen actual speech-level audio (per
// isVoiceLevel) in the last LIVENESS_FRAME_RECENT_MS but have not received
// a recognition result (transcribing/transcribed) from Azure in
// LIVENESS_TIMEOUT_MS, treat the connection as silently dead and force a
// reconnect instead of waiting for the silence modal to confuse the user.
//
// This is intentionally independent of SILENCE_TOLERANCE_MS. Silence and a
// dead connection are different failure modes: silence means nothing to
// react to (frames flowing but no VOICE), while a dead-while-speaking
// connection needs fast recovery regardless of how long silence is
// tolerated elsewhere. Gating on isVoiceLevel (rather than "any frame
// sent", which included pure background noise) is what makes it safe to
// keep this short — a genuinely quiet room never counts as "frames
// flowing" in the first place, so it can't misfire the way it used to.
export const LIVENESS_TIMEOUT_MS = 30000;
export const LIVENESS_FRAME_RECENT_MS = 5000;
export const LIVENESS_CHECK_INTERVAL_MS = 5000;

/**
 * Exponential backoff with +/- 25% jitter, capped at RECONNECT_BACKOFF_MAX_MS.
 * `attempt` is 1-indexed (1st reconnect attempt → 1).
 */
export function calculateReconnectBackoffMs(
  attempt: number,
  random: () => number = Math.random
): number {
  const exp = RECONNECT_BACKOFF_BASE_MS * Math.pow(2, Math.max(0, attempt - 1));
  const capped = Math.min(exp, RECONNECT_BACKOFF_MAX_MS);
  const jitterRange = capped * 0.25;
  const jitter = (random() * 2 - 1) * jitterRange;
  return Math.max(0, Math.round(capped + jitter));
}

/**
 * Liveness predicate. Returns true if we should declare the stream
 * dead and trigger a reconnect.
 *
 * Decision: speech-level frames have been flowing recently (user is
 * actually speaking, per isVoiceLevel — not just ambient noise) AND Azure
 * has not emitted a recognition result for too long.
 *
 * lastTurnEventAt should be initialized to the connection-open timestamp so
 * we don't false-positive in the brief window before the first event.
 */
export function isStreamLikelyDead(args: {
  lastTurnEventAt: number;
  lastFrameSentAt: number | null;
  now: number;
  thresholdMs?: number;
  frameRecentMs?: number;
}): boolean {
  const threshold = args.thresholdMs ?? LIVENESS_TIMEOUT_MS;
  const frameRecent = args.frameRecentMs ?? LIVENESS_FRAME_RECENT_MS;

  // No frames have ever been sent — the connection is open but the audio
  // graph isn't producing. Don't blame the connection for that.
  if (args.lastFrameSentAt == null) return false;

  // Frames stopped a while ago (user paused, mic ended, tab backgrounded).
  // The silence modal / track-ended handler covers this case; the
  // watchdog should stay out of it.
  if (args.now - args.lastFrameSentAt > frameRecent) return false;

  // Frames are flowing but Azure has been silent — dead.
  return args.now - args.lastTurnEventAt > threshold;
}
